import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { useAppSelector } from 'hooks';

import { apiSessionSseTicket, sseHost } from '~constants/api';
import { BACKGROUND_HEADER } from '~constants/session';

import { selectAuthenticated, selectReauthPrompt } from '~main/selectors/login';

interface SSEEvent {
  type: string;
  ref: string;
  ts: string;
}

interface UseSSEOptions {
  channels: string[];
  eventTypes: string[];
  onEvent: (event: SSEEvent) => void;
  enabled?: boolean;
}

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const BACKOFF_MULTIPLIER = 2;

const useSSE = ({ channels, eventTypes, onEvent, enabled = true }: UseSSEOptions) => {
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const reauthPrompt = useAppSelector(selectReauthPrompt);
  // While the password prompt is up the API refuses tickets, so hold the stream until it closes.
  const canConnect = isAuthenticated && reauthPrompt !== 'required';
  const [connected, setConnected] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const onEventRef = useRef(onEvent);

  onEventRef.current = onEvent;

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnected(false);
  }, []);

  const channelsKey = channels.join(',');
  const eventTypesKey = eventTypes.join(',');

  useEffect(() => {
    if (!enabled || !sseHost || channels.length === 0 || eventTypes.length === 0 || !canConnect) {
      cleanup();
      return;
    }

    // Everything async below checks this before touching state, so an effect that has been torn down (a
    // sign-out, a dependency change, an unmount) never opens a stream behind its own cleanup.
    const controller = new AbortController();

    const scheduleReconnect = () => {
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_DELAY_MS);
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    const connect = async () => {
      const channelParam = channels.join(',');

      // The browser session is a cookie the SSE host never sees, so each connection trades it for a
      // short-lived ticket. The ticket request is signed by the axios interceptor like any other.
      let ticket: string;
      try {
        const { data } = await axios.post(
          apiSessionSseTicket,
          {},
          { headers: { [BACKGROUND_HEADER]: '1' }, signal: controller.signal },
        );
        ({ ticket } = data.data);
      } catch (error) {
        if (controller.signal.aborted) return;
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          // Waiting on a password: the gate above reopens this effect once it is confirmed. Any other 401
          // means the session is gone, and the interceptor is already signing the user out.
          return;
        }
        setConnected(false);
        scheduleReconnect();
        return;
      }
      if (controller.signal.aborted) return;

      // Ticket is passed as a query param because the native EventSource API doesn't support custom headers.
      // TAV-2278 spec preferred @microsoft/fetch-event-source to use an Authorization header instead — deferred to TAV-2426.
      const url = `${sseHost}/events?token=${encodeURIComponent(ticket)}&channels=${encodeURIComponent(channelParam)}`;

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // System event: connection confirmed
      eventSource.addEventListener('connected', () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        setConnected(true);
      });

      // System event: server shutting down — close and immediately reconnect to a healthy pod
      eventSource.addEventListener('reconnect', () => {
        setConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        reconnectTimeoutRef.current = setTimeout(connect, 0);
      });

      // System event: missed events on reconnect — trigger a refresh
      eventSource.addEventListener('catchup', (messageEvent) => {
        try {
          const data = JSON.parse(messageEvent.data) as { reason: string; channels: string[] };
          // Notify via onEvent so the component can refetch
          onEventRef.current({ type: 'catchup', ref: data.channels.join(','), ts: new Date().toISOString() });
        } catch {
          // Non-JSON catchup — ignore
        }
      });

      // System event: the session this ticket was minted for has ended. A fresh ticket succeeds if it has been
      // extended or reconfirmed, and 401s (stopping the stream) if it has not.
      eventSource.addEventListener('session-expired', () => {
        setConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        reconnectTimeoutRef.current = setTimeout(connect, 0);
      });

      const systemEventTypes = new Set(['connected', 'reconnect', 'catchup', 'session-expired']);

      // Register listeners for each requested event type
      for (const type of eventTypes) {
        if (systemEventTypes.has(type)) continue;

        eventSource.addEventListener(type, (messageEvent) => {
          try {
            const data = JSON.parse(messageEvent.data) as SSEEvent;
            onEventRef.current(data);
          } catch {
            // Non-JSON event — ignore
          }
        });
      }

      eventSource.onerror = () => {
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);

        scheduleReconnect();
      };
    };

    connect();

    return () => {
      controller.abort();
      cleanup();
    };
  }, [enabled, channelsKey, eventTypesKey, cleanup, canConnect]);

  return { connected };
};

export default useSSE;
